import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { CnDialog, CnButton, CnBox } from '@sto/cn-ui';
import styles from './index.module.scss';
import CMarkdown from '../CMarkdown';
import ReasoningProcessOld from '../reasoning-process-old';

interface ActionItem {
  id: string;
  type?: string;
  content: string;
  status: string;
  toolExecResult?: string;
  costTime?: number;
  aiThought?: string;
  actionDetail?: string;
  toolCallList?: any[];
  aiThoughtList?: any[];
  seqId?: number;
}

interface TaskItem {
  id?: string;
  title?: string;
  desc?: string;
  actionList?: ActionItem[];
}

interface ReasoningProcessProps {
  actionList: ActionItem[];
  taskList?: TaskItem[];
  outputStatus?: string; // 输出状态：FINISHED、WIP
  thoughtProgressFinished?: boolean; // 思考进度是否完成
}

// 数据结构定义
type ToolCard = {
  status: 'SUCCESS' | 'PENDING' | 'FAILED';
  detail?: {
    type: string;
    content: string;
  };
};

type AiThoughtItem = {
  type: 'CARD' | 'MARKDOWN';
  title?: string;
  content?: string | null;
  datasource?: string[];
  question?: string;
  params?: string;
  toolCard?: ToolCard;
};

type ThoughtItem = {
  id: string;
  type: 'TOOL_CALL' | 'AI_THOUGHT';
  content: string | null;
  aiThoughtList?: string[] | AiThoughtItem[];
  taskCount?: number;
  status: 'SUCCESS' | 'PENDING' | 'FAILED';
  startTime?: number;
  finishTime?: number;
  costTime?: number;
  toolCallList?: any[];
};

type ReasoningProcessV3Props = {
  thoughtList: ThoughtItem[];
};

// 状态图标
const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'SUCCESS' || status === 'completed') {
    return (
      <svg width="16" height="16" viewBox="0 0 1024 1024" fill="none"><path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.2 0-372-166.8-372-372S306.8 140 512 140s372 166.8 372 372-166.8 372-372 372zm-56-232l-144-144 56-56 88 88 200-200 56 56-256 256z" fill="#00B54B"/></svg>
    );
  }
  return (
      <svg width="16" height="16" viewBox="0 0 1024 1024" fill="none"><path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.2 0-372-166.8-372-372S306.8 140 512 140s372 166.8 372 372-166.8 372-372 372zm-56-232l-144-144 56-56 88 88 200-200 56 56-256 256z" fill="#4E5969"/></svg>
  );
};

// 理解问题组件
const UnderstandingSection: React.FC<{ item: ThoughtItem }> = ({ item }) => {
  const [expanded, setExpanded] = useState(false);
  const isSuccess = item.status === 'SUCCESS';
  const hasThoughts = item.aiThoughtList && item.aiThoughtList.length > 0;

  useEffect(() => {
    setExpanded(!isSuccess);
  }, [isSuccess]);

  return (
    <div className={styles.section}>
      <div 
        className={styles.sectionHeader}
        onClick={() => hasThoughts && setExpanded(!expanded)}
        style={{ cursor: hasThoughts ? 'pointer' : 'default' }}
      >
        {isSuccess ? <StatusIcon status={item.status} /> : (
          <span className={styles.loadingDots}>
            <span className={styles.dot}></span>
            <span className={styles.dot}></span>
            <span className={styles.dot}></span>
          </span>
        )}
        <span className={`${styles.sectionTitle}`}>
          {isSuccess ? '已理解问题' : '正在进行'}
        </span>
        {hasThoughts && (
          <svg 
            className={`${styles.expandIcon} ${expanded ? styles.expanded : ''}`} 
            width="14" height="14" 
            viewBox="0 0 1024 1024"
          >
            <path d="M512 714.667c-8.533 0-17.067-2.134-23.467-8.534L147.2 364.8c-12.8-12.8-12.8-32 0-44.8 12.8-12.8 32-12.8 44.8 0L512 640 832 320c12.8-12.8 32-12.8 44.8 0 12.8 12.8 12.8 32 0 44.8L535.467 706.133c-6.4 6.4-14.934 8.534-23.467 8.534z" fill="#86909C"/>
          </svg>
        )}
      </div>
      {expanded && hasThoughts && (
        <div className={styles.sectionContent}>
          <div className={styles.sectionHeader}>
            <img className={styles.statusIcon} src="https://imgcdn.sto.cn/63D52BBB-F882-40-56x56-D0DF9294-2459-4F01-BE56-BD5367E7A48B.png" alt="" />
            <span className={`${styles.subSectionTitle} ${!isSuccess ? styles.shimmerEffect : ''}`}>
              理解问题
            </span>
          </div>
          {(item.aiThoughtList as any[]).map((thought, index) => thought?.content && (
            <div key={index} className={styles.thoughtItem}>
              <CMarkdown content={thought.content} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// 单工具详情组件
const SingleToolDetail: React.FC<{ tool: AiThoughtItem, showDetail?: (title: string, detail: any) => void }> = ({ tool, showDetail }) => {
  const hasDataSource = tool.datasource && tool.datasource.length > 0;
  
  return (
    <div className={styles.toolDetail}>
      {/* 匹配数据 */}
      {hasDataSource && (
        <div className={styles.stepItem}>
          <div className={styles.dataSectionHeader}>
            <span className={styles.dataSectionTitle}>匹配数据：</span>
          </div>
          <div className={styles.datasourceList}>
            {tool.datasource?.map((source, index) => (
              <div key={index} className={styles.datasourceItem}>
                <img className={styles.datasourceIcon} src="https://imgcdn.sto.cn/0350DAB8-A98D-43-48x48-fileIcon.png" alt="" />
                <span>{source}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 数据分析 */}
      <div className={styles.stepItem}>
        <div className={styles.dataSectionHeader}>
          <span className={styles.dataSectionTitle}>数据解析：</span>
          {showDetail ? <CnButton text type="primary" size="small" onClick={() => showDetail('数据解析', tool)}>查看详情</CnButton> : null}
        </div>
        <div className={styles.stepContent}>
          {tool.params && (
            <div className={styles.queryParams}>
             {!showDetail && <div className={styles.queryParamsLabel}>查询参数</div>}
              <div className={styles.queryParamsContent}>
                <CMarkdown content={tool.params} />
              </div>
            </div>
          )}
          {tool.toolCard?.detail && !showDetail && (
            <div className={styles.queryResult}>
              <div className={styles.queryParamsLabel}>查询结果</div>
              <div className={styles.queryResultContent}>
                <CMarkdown content={tool.toolCard?.detail?.content || ''} className={styles.customCMarkdown} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 多工具任务组件
const MultiToolTask: React.FC<{ tool: AiThoughtItem; index: number; showDetail?: (title: string, tool: AiThoughtItem) => void }> = ({ tool, showDetail }) => {
  const isSuccess = tool.toolCard?.status === 'SUCCESS';

  return (
    <div className={styles.taskItem}>
      <div className={styles.taskHeader}>
        {!isSuccess && <span className={styles.spinner}></span>}
        <span className={styles.taskTitle}>{tool.title}</span>
        <CnButton text type="primary" size="small" onClick={() => showDetail(tool.title, tool)}>查看详情</CnButton>
      </div>
      <div className={styles.taskContent}>
        <CMarkdown content={tool.content || (tool.datasource && tool.datasource.length > 0 ? tool.datasource[0] : '')} />
      </div>
    </div>
  );
};

// 查询数据组件
const QueryDataSection: React.FC<{ item: ThoughtItem }> = ({ item }) => {
  const [expanded, setExpanded] = useState(false);
  const isMultiTool = (item.taskCount || 0) > 1;
  const aiThoughtList = (item.aiThoughtList || []) as AiThoughtItem[];
  const isSuccess = item.status === 'SUCCESS';
  const hasContent = aiThoughtList && aiThoughtList.length > 0;

  useEffect(() => {
    setExpanded(!isSuccess);
  }, [isSuccess]);

  /** 展示工具调用的详情 */
  const showDetail = useCallback((title, tool) => {
    CnDialog.show({
      title: (
        <div className={styles.dialogTitle} title={title}>
          {title}
        </div>
      ),
      content: (
        <div className={styles.dialogContent}>
          <SingleToolDetail tool={tool} />
        </div>
      ),
      width: 800,
      footer: false,
    });
  }, []);

  return (
    <div className={styles.section}>
      <div 
        className={styles.sectionHeader}
        onClick={() => hasContent && setExpanded(!expanded)}
        style={{ cursor: hasContent ? 'pointer' : 'default' }}
      >
        {isSuccess ? <StatusIcon status={item.status} /> : (
          <span className={styles.loadingDots}>
            <span className={styles.dot}></span>
            <span className={styles.dot}></span>
            <span className={styles.dot}></span>
          </span>
        )}
        <span className={`${styles.sectionTitle}`}>
          {isSuccess ? '已经匹配到相关数据' : '正在进行'}
        </span>
        {hasContent && (
          <svg 
            className={`${styles.expandIcon} ${expanded ? styles.expanded : ''}`} 
            width="14" height="14" 
            viewBox="0 0 1024 1024"
          >
            <path d="M512 714.667c-8.533 0-17.067-2.134-23.467-8.534L147.2 364.8c-12.8-12.8-12.8-32 0-44.8 12.8-12.8 32-12.8 44.8 0L512 640 832 320c12.8-12.8 32-12.8 44.8 0 12.8 12.8 12.8 32 0 44.8L535.467 706.133c-6.4 6.4-14.934 8.534-23.467 8.534z" fill="#86909C"/>
          </svg>
        )}
      </div>
      {expanded && hasContent && (
        <div className={styles.sectionContent}>
          <div className={styles.sectionHeader}>
            <img className={styles.statusIcon} src="https://imgcdn.sto.cn/0BF4C52D-7591-43-56x56-B149FFFA-8A19-443B-A2F7-624D7C22577F.png" alt="" />
            <span className={`${styles.subSectionTitle} ${!isSuccess ? styles.shimmerEffect : ''}`}>
              查询数据
            </span>
          </div>
          {isMultiTool ? (
            // 多工具展示
            <div className={styles.multiToolContainer}>
              {aiThoughtList
                .map((tool, index) => (
                  <MultiToolTask key={index} tool={tool} index={index} showDetail={showDetail} />
                ))}
            </div>
          ) : (
            // 单工具展示
            <div className={styles.singleToolContainer}>
              {aiThoughtList
                .map((tool, index) => (
                  <SingleToolDetail key={index} tool={tool} showDetail={(title: string, tool: AiThoughtItem) => showDetail(title, tool)} />
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * 推理过程（第三版交互方式）
 * 展示的内容主要取aiThoughtList（刚子版）
 * @param props 
 * @returns 
 */
const ReasoningProcessNew: React.FC<ReasoningProcessProps> = ({ actionList, taskList = [], thoughtProgressFinished = false }) => {
  const [showTaskReasoning, setShowTaskReasoning] = useState(true); // 是否展示任务推理（taskList）
  const [showReasoning, setShowReasoning] = useState(true); // 思考推理（actionList）

  useEffect(() => {
    if (thoughtProgressFinished) {
      // 推理完成，收起整个思考推理模块
      setShowTaskReasoning(false);
      setShowReasoning(false);
    }
  }, [thoughtProgressFinished]);

  // 去重 actionList
  const deduplicatedActionList = useMemo(() => {
    const uniqueItems = new Map<string, ActionItem>();
    actionList?.forEach(item => {
      if (item?.id) {
        uniqueItems.set(item.id, item);
      }
    });
    return Array.from(uniqueItems.values());
  }, [actionList]);

  // 渲染步骤内容
  const renderSteps = (list: ActionItem[]) => {
    const isInitial = list?.some(item => ['default_id', 'default_thought_id'].includes(item.id));
    if (isInitial) {
      return <div className={styles.reasoningProcess}>
        <CnBox direction="row" align="center">
          <span className={styles.spinner}></span>
          <span>{list[0].content}</span>
        </CnBox>
      </div>;
    }
    return (
      <div className={styles.reasoningProcess}>
        {list.map((item, index) => {
          if (item.seqId === 1) {
            return <UnderstandingSection key={item.id || index} item={item as ThoughtItem} />;
          } else if (item.seqId === 2) {
            return <QueryDataSection key={item.id || index} item={item as ThoughtItem} />;
          }
          return null;
        })}
      </div>
    );
  };

  // 渲染 taskList
  if (taskList && taskList.length > 0) {
    return (
      <div className={styles.reasoningContainer}>
        <div className={styles.reasoningHeader} onClick={() => setShowTaskReasoning(v => !v)}>
          <span className={styles.reasoningTitle}>
            <img src="https://imgcdn.sto.cn/F6EC6438-3259-44-18x13-%E5%BD%A2%E7%8A%B6.svg" alt="" style={{ width: '18px', height: '13px', display: 'block', marginRight: '4px' }} />
            申懂懂多智能体{thoughtProgressFinished ? '已完成推理' : '推理中...'}
            <svg className={`${styles.arrow} ${showTaskReasoning ? styles.arrowUp : ''}`} viewBox="0 0 1024 1024" width="14" height="14" fill="#86909C">
              <path d="M512 714.667c-8.533 0-17.067-2.134-23.467-8.534L147.2 364.8c-12.8-12.8-12.8-32 0-44.8 12.8-12.8 32-12.8 44.8 0L512 640 832 320c12.8-12.8 32-12.8 44.8 0 12.8 12.8 12.8 32 0 44.8L535.467 706.133c-6.4 6.4-14.934 8.534-23.467 8.534z"></path>
            </svg>
          </span>
        </div>
        {showTaskReasoning && (
          <div className={styles.multipleStepsContainer}>
            {taskList.map((task, idx) => (
              <div key={task.id || idx} style={{ marginBottom: 6, background: '#F7F8FA', borderRadius: 8, padding: '12px 12px 0' }}>
                <div style={{ fontWeight: 500, color: '#7b68ee', marginBottom: 6 }}>步骤 {idx + 1}：{task.title || task.desc || '无标题'}</div>
                {task.actionList && task.actionList.length > 0 && renderSteps(task.actionList)}
                {task.desc && <div style={{ color: '#86909C', fontSize: 12, marginTop: 4 }}>{task.desc}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // 否则就渲染 actionList
  if (deduplicatedActionList && deduplicatedActionList.length > 0) {
    return (
      <div className={styles.reasoningContainer}>
        <div className={styles.reasoningHeader} onClick={() => setShowReasoning(v => !v)}>
          <span className={styles.reasoningTitle}>
            <img src="https://imgcdn.sto.cn/F6EC6438-3259-44-18x13-%E5%BD%A2%E7%8A%B6.svg" alt="" style={{ width: '18px', height: '13px', display: 'block', marginRight: '4px' }} />
            申懂懂多智能体{thoughtProgressFinished ? '已完成推理' : '推理中...'}
            <svg className={`${styles.arrow} ${showReasoning ? styles.arrowUp : ''}`} viewBox="0 0 1024 1024" width="14" height="14" fill="#86909C">
              <path d="M512 714.667c-8.533 0-17.067-2.134-23.467-8.534L147.2 364.8c-12.8-12.8-12.8-32 0-44.8 12.8-12.8 32-12.8 44.8 0L512 640 832 320c12.8-12.8 32-12.8 44.8 0 12.8 12.8 12.8 32 0 44.8L535.467 706.133c-6.4 6.4-14.934 8.534-23.467 8.534z"></path>
            </svg>
          </span>
        </div>
        {showReasoning && renderSteps(deduplicatedActionList)}
      </div>
    );
  }

  return null;
};

// 推理过程组件
const ReasoningProcessComp: React.FC<ReasoningProcessProps> = ({ actionList = [], taskList = [], thoughtProgressFinished = false }) => {

  // 判断是否是旧版交互方式
  const isOldVersion = useMemo(() => {
    if (actionList.length) {
      const hasNewField = actionList.some(item => {
        const { type, toolCallList, aiThoughtList } = item;
        // 工具调用，且新字段toolCallList有值，即是新版交互方式 或 思考推理，且新字段aiThoughtList有值，即是新版交互方式
        return (type === 'TOOL_CALL' && toolCallList && toolCallList.length > 0) || (type === 'AI_THOUGHT' && aiThoughtList && aiThoughtList.length > 0);
      });
      return !hasNewField;
    } else if (taskList.length) {
      const isNewVersion = taskList.some(item => {
        if (item.actionList && item.actionList.length) {
          const hasNewField = item.actionList.some(sitem => {
            const { type, toolCallList, aiThoughtList } = sitem;
            return (type === 'TOOL_CALL' && toolCallList && toolCallList.length > 0) || (type === 'AI_THOUGHT' && aiThoughtList && aiThoughtList.length > 0);
          });
          return !hasNewField;
        }
        return false;
      });
      return !isNewVersion;
    } else {
      return false;
    }
  }, [actionList, taskList]);

  if (isOldVersion) {
    // 旧版本数据结构，用旧版本展示
    return <ReasoningProcessOld actionList={actionList} taskList={taskList} thoughtProgressFinished={thoughtProgressFinished} />;
  } else {
    // 新版本数据结构，用新版本展示
    return <ReasoningProcessNew actionList={actionList} taskList={taskList} thoughtProgressFinished={thoughtProgressFinished} />;
  }
};

export default ReasoningProcessComp;
